import React, { useState, useMemo, useCallback } from "react";
import {
  CssBaseline,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  useMediaQuery,
  Typography,
  Avatar,
  Divider,
  Tooltip,
  Menu,
  MenuItem,

  Paper,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import "./middleContent/sidebar/Sidebar.css";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {

  faUser,
  faShoppingCart,
  faHouse,
  faSignOutAlt,
  faCog,
  faWallet,
  faGift,
  faCoins,

} from "@fortawesome/free-solid-svg-icons";

import WestIcon from "@mui/icons-material/West";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { styled, useTheme } from "@mui/material/styles";
import ProfileNavbar from "../../innerComponent/ProfileNavbar";
import HomeIcon from "@mui/icons-material/Home";
import Logo from '../../../assets/img/logo.png';
import { useSelector } from "react-redux";
import { userLogout } from "../../../apiCalls/apiHandler.js";

// Component prop types
const Index = ({ children }) => {
  const Authuser = useSelector((state) => state?.UserProfile?.allData)
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);



  // Media queries for responsive design
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isExtraLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Calculate drawer width once to prevent unnecessary re-renders
  const drawerWidth = useMemo(() => {
    if (open) {
      if (isExtraLargeScreen) return '300px';
      if (isLargeScreen) return '280px';
    }
    return '280px';
  }, [open, isExtraLargeScreen, isLargeScreen]);

  // Handlers for drawer open/close
  const handleDrawerOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Menu handlers
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };


  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  // Animation mixins for drawer
  const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  });

  const closedMixin = (theme) => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(9)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
      width: `calc(${theme.spacing(10)} + 1px)`,
    },
  });

  // Styled Drawer component
  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
  })(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": {
        ...openedMixin(theme),
        backgroundColor: "#002865",
        borderRight: "none",
        boxShadow: "4px 0 20px rgba(0,0,0,0.1)",
      },
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": {
        ...closedMixin(theme),
        backgroundColor: "#002865",
        borderRight: "none",
        boxShadow: "4px 0 20px rgba(0,0,0,0.1)",
      },
    }),
  }));

  // Navigation menu items
  const menuItems = useMemo(() => [
    {
      name: "Dashboard",
      path: "/",
      icon: faHouse,
      breadCrumbText: "Dashboard",
    },
    {
      name: "Gifts Cards",
      path: "/card-list",
      icon: faGift,
      breadCrumbText: "Gifts Cards",
    },
    {
      name: "Orders",
      path: "/profile/order",
      icon: faShoppingCart,
      breadCrumbText: "Your Orders",
    },
    {
      name: "Statement",
      path: "/profile/statement",
      icon: faWallet,
      breadCrumbText: "Wallet",
    },
    {
      name: "Profile",
      path: "/profile/user-profile",
      icon: faUser,
      breadCrumbText: "Profile",
    },
  ], []);

  // Get current route safely
  const currentRoute = useCallback(() => {
    const pathSegments = location?.pathname.split("/");
    return pathSegments[pathSegments.length - 1] || "";
  }, [location?.pathname]);

  // Check if current item is active
  const isActive = useCallback((itemName) => {
    const current = currentRoute().toLowerCase();
    return current === itemName.toLowerCase().replace(/\s+/g, '-');
  }, [currentRoute]);

  // Get page title from current route
  const getPageTitle = useCallback(() => {
    const currentPath = location?.pathname;
    const matchedItem = menuItems.find(item => currentPath === item.path);
    return matchedItem ? matchedItem.name : "Dashboard";
  }, [location?.pathname, menuItems]);

  return (
    <Box sx={{ display: "flex", backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
      <CssBaseline />

      {/* Sidebar Drawer */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            borderRadius: "0 16px 16px 0",
            display: { xs: 'none', sm: 'block' }
          }
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflowY: "auto",
            overflowX: "hidden",
            py: 3,
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "2px",
            },
          }}
        >
          {/* Company Logo Section */}
          <Box
          sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "flex-start" : "center",
              width: "100%",
              px: open ? 3 : 1,
              mb: 4,
              gap: 2,
            }}
             onClick={() => navigate("/")}
          >
            {open ? (
              <Box
              sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  backgroundColor: "white.main",
                  borderRadius: 4,
                }}
              >
                <Avatar
                  src={Logo}
                  alt="99Gift"
                  sx={{
                    width: 190,
                    height: 120,
                    backgroundColor: "white.main",
                    "& img": {
                      objectFit: "cover",
                      height:190,
                      width: "100%",
                    },
                  }}
                />
              </Box>
            ) : (
              <Avatar
                src={Logo}
                alt="99Gift"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "14px",
                  border: "2px solid rgba(255,255,255,0.2)",
                  bgcolor: "white",
                  p: 1,
                }}
              />
            )}
          </Box>

          <Divider sx={{ width: "85%", bgcolor: "rgba(255,255,255,0.08)", mb: 1 }} />

          {/* Navigation Menu */}
          <List sx={{ width: "100%", px: open ? 2 : 1, flex: 1 }}>
            {menuItems.map((item, index) => (
              <NavLink
                to={item.path}
                key={`user-route-${index}`}
                style={{ textDecoration: "none" }}
              >
                <Tooltip
                  title={!open ? item.name : ""}
                  placement="right"
                  arrow
                >
                  <ListItem
                    disablePadding
                    sx={{
                      mb: 0.5,
                      borderRadius: "12px",
                      backgroundColor: isActive(item.name)
                        ? "rgba(255, 215, 0, 0.15)"
                        : "transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: isActive(item.name)
                          ? "rgba(255, 215, 0, 0.15)"
                          : "rgba(255, 255, 255, 0.08)",
                      },
                      position: "relative",
                      "&::before": isActive(item.name) ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "4px",
                        height: "28px",
                        backgroundColor: "#FFD700",
                        borderRadius: "0 4px 4px 0",
                      } : {},
                    }}
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        px: open ? 3 : 2,
                        py: 1.5,
                        justifyContent: open ? "flex-start" : "center",
                        borderRadius: "12px",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 2 : 0,
                          justifyContent: "center",
                          color: isActive(item.name)
                            ? "#FFD700"
                            : "rgba(255,255,255,0.7)",
                          transition: "color 0.2s ease",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={item.icon}
                          style={{
                            width: "18px",
                            height: "18px",
                          }}
                        />
                      </ListItemIcon>

                      {open && (
                        <ListItemText
                          primary={item.name}
                          sx={{
                            "& .MuiTypography-root": {
                              fontSize: "15px",
                              fontWeight: isActive(item.name) ? 600 : 400,
                              color: isActive(item.name)
                                ? "#FFFFFF"
                                : "rgba(255,255,255,0.8)",
                              transition: "color 0.2s ease",
                            },
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
              </NavLink>
            ))}
          </List>

          {/* Bottom Section - Logout */}
          <Divider sx={{ width: "85%", bgcolor: "rgba(255,255,255,0.08)", mb: 2 }} />

          <Box sx={{ width: "100%", px: open ? 2 : 1 }}>
            <Tooltip title={!open ? "Logout" : ""} placement="right" arrow>
              <ListItem
                disablePadding
                sx={{
                  borderRadius: "12px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255, 0, 0, 0.1)",
                  },
                }}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    px: open ? 3 : 2,
                    py: 1.5,
                    justifyContent: open ? "flex-start" : "center",
                    borderRadius: "12px",
                  }}
                  onClick={() => userLogout()}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 2 : 0,
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.6)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faSignOutAlt}
                      style={{
                        width: "18px",
                        height: "18px",
                      }}
                    />
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary="Logout"
                      sx={{
                        "& .MuiTypography-root": {
                          fontSize: "15px",
                          fontWeight: 400,
                          color: "rgba(255,255,255,0.7)",
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          </Box>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: "#FFF",
          borderRadius: { xs: 0, sm: "24px 0 0 0" },
          px: { xs: 0, sm: 3, lg: 4 },
          py: { xs: 0, sm: 3 },
          width: `calc(100% - ${open ? drawerWidth : '73px'})`,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          minHeight: "100vh",
        }}
      >
        {/* Professional Enhanced Header */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 4,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            backgroundColor: "white",
            borderRadius: "16px",
            flexWrap: "wrap",
            gap: 2,
            minHeight: "72px",
          }}
        >
          {/* Left Section - Toggle & Breadcrumb */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {!isMobile && (
              <IconButton
                onClick={open ? handleDrawerClose : handleDrawerOpen}
                sx={{
                  color: "#002865",
                  backgroundColor: "rgba(0,40,101,0.06)",
                  "&:hover": {
                    backgroundColor: "rgba(0,40,101,0.12)",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                  width: 40,
                  height: 40,
                }}
              >
                {open ? <WestIcon fontSize="small" /> : <ArrowRightAltIcon fontSize="small" />}
              </IconButton>
            )}

            {/* MOBILE ONLY HOME */}
            {isMobile && (
              <Box
                onClick={() => navigate("/")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  color: "#002865",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <HomeIcon fontSize="small" />
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  Home
                </Typography>
              </Box>
            )}

            {/* Page Title & Breadcrumb */}
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "#1a1a2e",
                  fontSize: { xs: "20px", md: "24px" },
                  lineHeight: 1.2,
                  letterSpacing: "-0.5px",
                  fontFamily: "Be Vietnam Pro"
                }}
              >
                {getPageTitle()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#6b7280",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontFamily: "Be Vietnam Pro"
                }}
              >
                <span style={{ opacity: 0.5 }}>•</span>
                {location?.pathname === "/"
                  ? "Welcome back! Manage your gifts and orders"
                  : `Manage your ${getPageTitle().toLowerCase()}`}
              </Typography>
            </Box>
          </Box>

          {/* Right Section - Actions & Profile */}
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            ml: "auto",
          }}>
            {/* Wallet Section */}
            <Tooltip title="Wallet Balance" arrow>
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: "rgba(233, 179, 103, 0.1)",
                  borderRadius: "10px",
                  border: "1px solid rgba(233, 179, 103, 0.2)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(233, 179, 103, 0.2)",
                  },
                }}

              >
                <FontAwesomeIcon
                  icon={faCoins}
                  style={{
                    color: "#e9b367",
                    fontSize: "16px",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#002865",
                    fontSize: "14px",
                    fontFamily: "Be Vietnam Pro"
                  }}
                >
                  AED {Authuser?.walletDatum?.amount?.toLocaleString()}
                </Typography>
              </Box>
            </Tooltip>

            {/* User Profile */}
            <Box sx={{ display: "flex", alignItems: "center" }}>

                <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    cursor: "pointer",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "12px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      backgroundColor: "#002865",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "14px",
                      fontFamily: "Be Vietnam Pro"
                    }}
                  >
                    {Authuser?.firstName?.split(" ")?.slice(0, 2).map(word => word.charAt(0)).join("").toUpperCase()}
                  </Avatar>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1a1a2e",
                        fontSize: "14px",
                        lineHeight: 1.2,
                        fontFamily: "Be Vietnam Pro"
                      }}
                    >
                      {Authuser?.firstName}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#6b7280",
                        fontSize: "11px",
                        lineHeight: 1.2,
                        fontFamily: "Be Vietnam Pro"
                      }}
                    >
                      {Authuser?.email}
                    </Typography>
                  </Box>
                </Box>

            </Box>

            {/* Mobile Profile */}
            <Box sx={{
              display: { xs: "flex", md: "none" },
            }}>
              <ProfileNavbar />
            </Box>
          </Box>
        </Paper>

        {/* Profile Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.05)",
            }
          }}
        >
          <MenuItem onClick={() => { navigate("/profile/user-profile"); handleProfileMenuClose(); }}>
            <FontAwesomeIcon icon={faUser} style={{ marginRight: 12, width: 16, color: "#6b7280" }} />
            My Profile
          </MenuItem>
          <MenuItem onClick={() => { navigate("/profile/wallet"); handleProfileMenuClose(); }}>
            <FontAwesomeIcon icon={faWallet} style={{ marginRight: 12, width: 16, color: "#6b7280" }} />
            Wallet
          </MenuItem>
          <MenuItem onClick={() => { navigate("/profile/settings"); handleProfileMenuClose(); }}>
            <FontAwesomeIcon icon={faCog} style={{ marginRight: 12, width: 16, color: "#6b7280" }} />
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { navigate("/logout"); handleProfileMenuClose(); }} sx={{ color: "#ef4444" }}>
            <FontAwesomeIcon icon={faSignOutAlt} style={{ marginRight: 12, width: 16 }} />
            Logout
          </MenuItem>
        </Menu>

        {/* Notification Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1.5,
              width: 320,
              maxHeight: 400,
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.05)",
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
            <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
              Notifications
            </Typography>
          </Box>
          <MenuItem sx={{ py: 1.5 }}>
            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                🎁 Gift Order Confirmed
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                Your gift order #1234 has been confirmed
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#9ca3af", mt: 0.5 }}>
                2 hours ago
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem sx={{ py: 1.5 }}>
            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                💰 Wallet Refund
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                ₹500 has been added to your wallet
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#9ca3af", mt: 0.5 }}>
                1 day ago
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem sx={{ py: 1.5 }}>
            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                ⭐ Points Earned
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                You earned 50 points from your recent purchase
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "#9ca3af", mt: 0.5 }}>
                3 days ago
              </Typography>
            </Box>
          </MenuItem>
          <Box sx={{ p: 1.5, borderTop: "1px solid rgba(0,0,0,0.06)", textAlign: "center" }}>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#002865",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": { opacity: 0.7 }
              }}
              onClick={() => { navigate("/profile/notifications"); handleNotificationClose(); }}
            >
              View All Notifications
            </Typography>
          </Box>
        </Menu>

        {/* Content Container */}
        <Box
          sx={{
            backgroundColor: "white",
            p: { xs: 2, sm: 3 },
            borderRadius: { xs: 0, sm: "16px" },
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            minHeight: "calc(100vh - 180px)",
            width: "100%",
            overflow: "auto",
            border: "1px solid rgba(0,0,0,0.04)",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.1)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(0,0,0,0.02)",
            },
          }}
        >
          <Outlet>{children}</Outlet>
        </Box>
      </Box>
    </Box>
  );
};

export default Index;
